import PregnantWomanOutlinedIcon from '@mui/icons-material/PregnantWomanOutlined';

/**
 * Diástase abdominal: teste, localização e largura da separação dos retos.
 * Referências clínicas:
 * - localização e largura: classificação da German Hernia Society e da
 *   International Endohernia Society (Reinpold W et al., "Classification of
 *   Rectus Diastasis", Front Surg 2019;6:1), com largura W1 (< 3 cm),
 *   W2 (3 a 5 cm) e W3 (> 5 cm); setores agrupados acima, na altura e abaixo
 *   do umbigo, ou em toda a linha alba;
 * - teste: palpação da linha alba em flexão do tronco (teste dos dedos),
 *   usada na fisioterapia pélvica e no pós-parto.
 * - tipo A a D: classificação de Nahas (Nahas FX, Plast Reconstr Surg
 *   2001;108:1787-95), da cirurgia plástica, que descreve a causa da
 *   deformidade músculo-aponeurótica para indicar a correção. Campo opcional,
 *   para quem trabalha com a cirurgia ou o encaminhamento.
 * Textos nossos.
 *
 * Os valores precisam ser os mesmos de public/api/scales/catalog/diastasis_recti.php.
 */
const diastasisRecti = {
  key: 'diastasis_recti',
  name: 'Diástase abdominal',
  description: 'Teste, localização, largura e tipo (A a D) da separação dos retos',
  details: 'Registra o resultado do teste da diástase dos músculos retos do abdome, onde a separação aparece (acima, na altura ou abaixo do umbigo, ou em toda a linha alba) e a largura, pela classificação da Sociedade Alemã de Hérnia: W1 até 3 cm, W2 de 3 a 5 cm e W3 acima de 5 cm. Opcionalmente, registra o tipo A a D de Nahas, usado na cirurgia plástica. Serve ao acompanhamento no pós-parto e na fisioterapia pélvica. Marque só o que avaliou; o card mostra o resultado da última consulta e a aba Saúde guarda a evolução.',
  kind: 'fields',
  icon: PregnantWomanOutlinedIcon,
  accent: 'purple',
  fields: [
    {
      key: 'test',
      label: 'Teste',
      options: [
        { value: 'positive', label: 'Positivo', help: 'Separação palpável entre os retos, com afundamento ou abaulamento da linha alba ao elevar o tronco.' },
        { value: 'negative', label: 'Negativo', help: 'Retos próximos, sem separação relevante à palpação.' },
      ],
    },
    {
      key: 'location',
      label: 'Localização',
      options: [
        { value: 'supraumbilical', label: 'Acima do umbigo', caption: 'Supraumbilical', image: 'diastase-1.png', help: 'Separação entre o esterno e o umbigo.' },
        { value: 'umbilical', label: 'Na altura do umbigo', caption: 'Umbilical', image: 'diastase-2.png', help: 'Separação ao redor do umbigo, a localização mais comum.' },
        { value: 'infraumbilical', label: 'Abaixo do umbigo', caption: 'Infraumbilical', image: 'diastase-3.png', help: 'Separação entre o umbigo e o púbis. Rara de forma isolada.' },
        { value: 'complete', label: 'Toda a linha alba', caption: 'Completa', image: 'diastase-4.png', help: 'Separação do esterno ao púbis.' },
      ],
    },
    {
      key: 'width',
      label: 'Largura',
      options: [
        { value: 'W1', label: 'W1', caption: 'Menos de 3 cm', help: 'Separação pequena, abaixo de 3 cm.' },
        { value: 'W2', label: 'W2', caption: '3 a 5 cm', help: 'Separação moderada, de 3 a 5 cm.' },
        { value: 'W3', label: 'W3', caption: 'Mais de 5 cm', help: 'Separação grande, acima de 5 cm.' },
      ],
    },
    {
      key: 'nahas',
      label: 'Tipo (Nahas)',
      options: [
        { value: 'A', label: 'Tipo A', caption: 'Diástase pós-gestação', help: 'Afastamento dos retos depois da gravidez. Corrigido com plicatura da aponeurose dos retos.' },
        { value: 'B', label: 'Tipo B', caption: 'Com flacidez lateral', help: 'Diástase com flacidez da aponeurose abaixo do umbigo e nas laterais. Pede plicatura dos retos e da aponeurose lateral.' },
        { value: 'C', label: 'Tipo C', caption: 'Inserção lateral dos retos', help: 'Retos inseridos mais para os lados nas costelas, de nascença. Corrigido com avanço dos músculos retos.' },
        { value: 'D', label: 'Tipo D', caption: 'Com cintura pouco definida', help: 'Diástase com cintura pouco marcada, comum no sobrepeso. Pede plicatura dos retos e avanço dos oblíquos externos.' },
      ],
    },
  ],
};

export default diastasisRecti;
